/**
 * What the AI disclosure names, and the confirmation a non-owner types to
 * delete only their own user.
 *
 * Shared by the Worker and the browser so the notice, the stored version and
 * the mobile client cannot drift. The providers below are the ones the code
 * actually calls — see services/ai/client.ts and services/ai/magic-quote/provider.ts.
 */

/** Bump when the providers or the data sent to them change, so acceptance is asked again. */
export const AI_DISCLOSURE_VERSION = '2026-10-14-voicemail-1';

/** What a team member who is not an owner types to delete only their user. */
export const ACCOUNT_DELETION_MEMBER_CONFIRMATION = 'DELETE';

/** Public support address. Never a personal mailbox. */
export const SUPPORT_EMAIL = 'hello@blastoclean.com';

export interface AiDisclosureProvider {
  id: 'anthropic' | 'cloudflare_workers_ai';
  name: string;
  /** Which product features send work here. */
  usedFor: string;
  /** What leaves the account for that provider. */
  sent: string;
}

/**
 * Anthropic receives text and photos: Magic Quote's walkthrough photos, the
 * photos a team member takes for a Quick Quote, and the photos customers send
 * for a photo quote, and voicemail transcripts. Cloudflare Workers AI receives
 * audio (walkthrough recordings and voicemails) and runs OpenAI Whisper there. Audio is not sent to OpenAI's own API,
 * and this app does not call the OpenAI API.
 */
export const AI_DISCLOSURE_PROVIDERS: readonly AiDisclosureProvider[] = [
  {
    id: 'anthropic',
    name: 'Anthropic',
    usedFor:
      'Quick Quote, Magic Quote reasoning, photo quotes, the help assistant, and customer conversation drafts, visit moves, visit instruction suggestions, and empty quote draft notes, including those prepared from a voicemail transcript',
    sent: 'Text you type: quote descriptions, answers, and help questions, plus the product documentation the help assistant is grounded in. Photos your team adds to a Quick Quote. Magic Quote walkthrough photos. The photos your customers send for a photo quote, with what they tell you about the home. Conversation replies send your company name, up to 8 recent message excerpts of 1,200 characters each, and dates and status for up to 3 upcoming visits. Visit moves and instruction suggestions send the selected visit date, time, status and timezone, the current date reference, and one latest customer message excerpt of up to 1,200 characters. Internal notes for an empty quote draft send one latest message excerpt of up to 1,200 characters and no visit or financial metadata. A voicemail transcript is treated as a customer message: its triage sends up to 2,000 characters of it with dates and status for up to 3 upcoming visits, and drafts and suggestions send it under the limits above. These action prompts may include your company name and language. Excerpts may contain personal information written in the messages. Structured property addresses and contact details are not added to these conversation prompts. Prices are calculated from your task library, not by the model.',
  },
  {
    id: 'cloudflare_workers_ai',
    name: 'Cloudflare Workers AI',
    usedFor: 'Magic Quote voice transcription and voicemail transcripts',
    sent: 'Audio recorded during a Magic Quote walkthrough, and voicemails callers leave on your company number when voicemail transcription is on. Transcription uses OpenAI Whisper running on Cloudflare Workers AI. The audio is not sent to OpenAI’s API.',
  },
];
